import React, { useMemo } from 'react';
import { Box, Alert, Typography, Card, CardContent } from '@mui/material';
import * as Icons from '../constants/icons';
import { PAGE_FILL_HEIGHT } from '../constants/layout';
import LoadingView from '../components/LoadingView';
import { useConfirm } from '../providers/ConfirmProvider';
import { useNotification } from '../providers/NotificationProvider';
import { useRecruitmentPlan } from '../hooks/useRecruitmentPlan';
import { MONTH_ORDER, fiscalYearToPeriod } from '../components/profitAndLoss/constants';
import { computeDeptSeries } from '../components/recruitmentPlan/helpers';
import RecruitmentPlanFilterHeader from '../components/recruitmentPlan/RecruitmentPlanFilterHeader';
import RecruitmentPlanGrid from '../components/recruitmentPlan/RecruitmentPlanGrid';

/**
 * 人事採用 計画入力ページ。
 *
 * 部門別に、実績在籍数 (ベースライン) へ月次の採用予定 (+) ・退職予定 (-) を積み上げ、
 * 計画在籍数を予算バージョン単位で版管理する。
 * ヘッダー (年度・バージョン・保存) は常時表示し、グリッド領域のみローディング/エラーを切り替える。
 */
export default function RecruitmentPlanManagement() {
  const plan = useRecruitmentPlan();
  const confirm = useConfirm();
  const notify = useNotification();

  const { planRows, edits, loading, saving, error, fiscalYear, versionCode, save } = plan;

  // 全社合計サマリー (期首実績在籍・通期採用予定・期末計画在籍)。増員計画のため退職は扱わない
  const summary = useMemo(() => {
    let periodStartHeadcount = 0;
    let periodEndPlanned = 0;
    let yearHires = 0;
    const firstMonth = MONTH_ORDER[0]; // 9月 (期首)
    const lastMonth = MONTH_ORDER[MONTH_ORDER.length - 1]; // 8月 (期末)
    planRows.forEach((row) => {
      const series = computeDeptSeries(row, edits);
      series.forEach((s) => {
        yearHires += s.hires;
        if (s.m === firstMonth) periodStartHeadcount += s.headcount;
        if (s.m === lastMonth) periodEndPlanned += s.planned;
      });
    });
    return { periodStartHeadcount, periodEndPlanned, yearHires };
  }, [planRows, edits]);

  const handleSave = async () => {
    const ok = await confirm({
      title: '採用計画の保存',
      message: `${fiscalYear}年度（${fiscalYearToPeriod(fiscalYear)}期）の採用計画を保存します。よろしいですか？`,
    });
    if (!ok) return;
    const success = await save();
    if (success) notify.success('採用計画を保存しました。');
    else notify.error('採用計画の保存に失敗しました。');
  };

  const summaryCards = [
    { title: '期首実績在籍', value: summary.periodStartHeadcount, unit: '名', icon: <Icons.Groups sx={{ fontSize: '18px' }} />, color: '#eff6ff', iconColor: '#3b82f6' },
    { title: '通期採用予定', value: summary.yearHires, unit: '名', icon: <Icons.PersonAdd sx={{ fontSize: '18px' }} />, color: '#ecfdf5', iconColor: '#059669', signed: true },
    { title: '期末計画在籍', value: summary.periodEndPlanned, unit: '名', icon: <Icons.TrendingUp sx={{ fontSize: '18px' }} />, color: '#f5f3ff', iconColor: '#8b5cf6' },
  ];

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', height: PAGE_FILL_HEIGHT, boxSizing: 'border-box', overflow: 'hidden' }}>
      <Box sx={{ width: '92%', mt: 0, display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0 }}>
        <RecruitmentPlanFilterHeader plan={plan} onSave={handleSave} />

        {error && <Alert severity="warning" sx={{ width: '100%', borderRadius: '8px' }}>{error}</Alert>}

        {/* 全社合計サマリー */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
          {summaryCards.map((c, idx) => {
            const display = c.signed ? `${c.value >= 0 ? '+' : ''}${c.value.toLocaleString()}` : c.value.toLocaleString();
            const valColor = c.signed ? (c.value > 0 ? '#059669' : '#1e293b') : '#1e293b';
            return (
              <Card key={idx} sx={{ borderRadius: '10px', boxShadow: '0 2px 4px -1px rgb(0 0 0 / 0.08)', border: '1px solid #f1f5f9' }}>
                <CardContent sx={{ px: 1.75, py: 1, '&:last-child': { pb: 1 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ p: 0.6, borderRadius: '8px', backgroundColor: c.color, color: c.iconColor, display: 'flex' }}>
                      {c.icon}
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748b', lineHeight: 1.2 }}>
                        {c.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '18px', fontWeight: 800, color: valColor, lineHeight: 1.2 }}>
                          {display}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>
                          {c.unit}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* グリッド領域 (残り高さいっぱい・テーブル内スクロール。ローディングはオーバーレイ) */}
        <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {!loading && planRows.length > 0 && (
            <RecruitmentPlanGrid
              planRows={planRows}
              edits={edits}
              setEdit={plan.setEdit}
              addDept={plan.addDept}
              addPosition={plan.addPosition}
              addBranch={plan.addBranch}
              addedBranches={plan.addedBranches}
              availablePositions={plan.availablePositions}
              fiscalYear={fiscalYear}
            />
          )}

          {!loading && !error && versionCode && planRows.length === 0 && (
            <Box sx={{ mt: 4, p: 5, border: '2px dashed #cbd5e1', borderRadius: '12px', textAlign: 'center' }}>
              <Typography color="textSecondary">部門データが取得できませんでした。</Typography>
            </Box>
          )}

          {loading && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.65)', borderRadius: '12px', backdropFilter: 'blur(1px)' }}>
              <LoadingView message="計画データ取得中..." height="auto" size={50} />
            </Box>
          )}

          {saving && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 21, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '12px', backdropFilter: 'blur(1px)' }}>
              <LoadingView message="採用計画を保存中..." height="auto" size={50} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
